/**
 * HKDF-SHA256 (RFC 5869) - Web Crypto 実装。
 *
 * 用途 (KEY_HIERARCHY.md §3):
 *   - MK (Master Key, 32B) を HKDF で 2 系統に拡張する:
 *     - SMK_enc = HKDF(MK, info="ookey/v1/smk-enc", length=32B)  → AES-GCM 鍵 (Symmetric Key 包装用)
 *     - SMK_mac = HKDF(MK, info="ookey/v1/smk-mac", length=32B)  → 将来的な MAC / signature 用
 *
 * 仕様:
 *   - Hash: SHA-256
 *   - Salt: 任意 (省略時は全 0、SP 800-56C で問題なし)
 *   - Info: domain separation 用文字列 (constants.HKDF.INFO_*)
 *   - Output: 任意長 (32B 推奨)
 *   - importKey は extractable=false (派生鍵 bytes を JS ヒープに残さない)
 *
 * Web Crypto 注意:
 *   - 'HKDF' アルゴリズムでの importKey + deriveBits を使う
 *   - deriveKey ではなく deriveBits を使い、用途別 importKey は呼び出し側 (aes-gcm.ts) で行う
 */
import { HKDF } from './constants'
import { utf8ToBytes } from './encoding'

const subtle = (): SubtleCrypto => {
  if (typeof globalThis.crypto?.subtle === 'undefined') {
    throw new Error('Web Crypto API (crypto.subtle) is not available')
  }

  return globalThis.crypto.subtle
}

/**
 * HKDF-SHA256 で IKM から派生鍵 bytes を取り出す。
 *
 * @param ikm    入力鍵材料 (Master Key 等)
 * @param info   domain separation 文字列 ("ookey/v1/smk-enc" 等)
 * @param length 出力 byte 数 (≦ 8160 = 255 * 32)
 * @param salt   省略時は SHA-256 出力長と同じ全 0 (RFC 5869 §3.1)
 */
export const hkdfSha256 = async (
  ikm: Uint8Array,
  info: Uint8Array,
  length: number,
  salt?: Uint8Array,
): Promise<Uint8Array> => {
  if (!Number.isInteger(length) || length <= 0 || length > 8160) {
    throw new RangeError(`hkdf length out of range: ${length}`)
  }
  if (ikm.length === 0) {
    throw new RangeError('hkdf IKM must not be empty')
  }

  const ikmKey = await subtle().importKey(
    'raw',
    ikm as Uint8Array<ArrayBuffer>,
    { name: 'HKDF' },
    /* extractable */ false,
    ['deriveBits'],
  )

  const params: HkdfParams = {
    name: 'HKDF',
    hash: HKDF.HASH,
    salt: (salt ?? new Uint8Array(0)) as Uint8Array<ArrayBuffer>,
    info: info as Uint8Array<ArrayBuffer>,
  }

  const derived = await subtle().deriveBits(params, ikmKey, length * 8)

  return new Uint8Array(derived)
}

/**
 * Stretched Master Key の "enc" 系統 (Symmetric Key 包装用) を派生する。
 * 出力は 32 byte 固定。
 */
export const deriveStretchedMasterKeyEnc = (
  masterKey: Uint8Array,
  salt?: Uint8Array,
): Promise<Uint8Array> => hkdfSha256(masterKey, utf8ToBytes(HKDF.INFO_SMK_ENC), 32, salt)

/**
 * Stretched Master Key の "mac" 系統 (将来の HMAC 用) を派生する。
 * 出力は 32 byte 固定。Phase 3 では未使用、Phase 4 で導入予定。
 */
export const deriveStretchedMasterKeyMac = (
  masterKey: Uint8Array,
  salt?: Uint8Array,
): Promise<Uint8Array> => hkdfSha256(masterKey, utf8ToBytes(HKDF.INFO_SMK_MAC), 32, salt)
